<script setup lang="ts">
/**
 * ✕ The modal close button.
 *
 * v7.69 (per Javi: "remove the tap anywhere to go back and instead put a close X at the top right
 * corner") put a plain ✕ inside each card. v7.70 (per Javi: "match the aesthetics, similar to the
 * minimize route preview button, and have its center lay exactly on the corner, so it looks like it's
 * overlapping the corner"): it is the route preview's gold medallion (draft.vue .preview-chevron) with a
 * drawn ✕, and its centre sits on the card's top-right corner.
 *
 * Usage is unchanged: <ModalClose @close="..."/> anywhere inside the card (first child by convention).
 * The component leaves an invisible anchor there and moves the button into the card's PARENT - the
 * modal's backdrop - because half of it hangs outside the card: inside a card that scrolls or clips,
 * it was cut off, and v7.69's in-card ✕ pushed a sideways scrollbar onto the Collection's dossier. It
 * follows the corner as the card resizes, moves or animates in, and stays whole on screen when the
 * card reaches the viewport's edge.
 *
 * v7.79 (per Javi: "make the x close button a little bigger ... slightly move it inside the box some more so it's
 * not poking out the corner so much"): the button is a 48 px tap target and the medallion one size up - 40 px (34 px
 * on a phone, the old desktop size). Its centre sits --close-inset (8 px, main.css :root) in from the corner along
 * the diagonal: every painted frame cuts its corner as a bevel 5-9 px in, so the medallion now rests ON the frame
 * instead of hanging off the box corner. The `inset` prop overrides the var. When the card reaches the screen's edge
 * (a phone), the tap box slides inward to stay on screen and the medallion keeps its place inside it (--mx/--my) -
 * an off-screen box would make the backdrop scroll sideways.
 *
 * The corner is the PAINTED frame's: a parchment card (.card-ornate) draws its crest above the frame
 * line, so it sets --close-corner-ratio (the frame's top edge, as a fraction of the card's width -
 * the crest art scales with the width) and the medallion drops to that line (main.css).
 */
const props = withDefaults(defineProps<{ testid?: string; label?: string; disabled?: boolean; inset?: number }>(), {
  testid: 'modal-close',
  label: undefined,
  disabled: false,
  inset: undefined,
})
const emit = defineEmits<{ close: [] }>()
const { t } = useLocale()
// 📱 M6: Android's back closes the newest card exactly as this ✕ would
// (useBackStack); a disabled ✕ holds it.
useBackLayer(`close:${props.testid}`, () => true, () => {
  if (props.disabled) return false
  emit('close')
})

const anchor = ref<HTMLElement | null>(null)
const host = ref<HTMLElement | null>(null)
const button = ref<HTMLButtonElement | null>(null)
const spot = ref<{ left: number; top: number; ox: number; oy: number; fixed: boolean } | null>(null)
let card: HTMLElement | null = null
let observer: ResizeObserver | undefined
let timer: ReturnType<typeof setInterval> | undefined
let frame = 0
/** How close to the screen's (the backdrop's) edge the button may come. */
const EDGE = 2
/** ✕ v7.79: the centre's diagonal inset when the card sets no --close-inset (main.css :root sets 8px). */
const INSET = 8

function place() {
  const h = host.value
  if (!card || !h) return
  const c = card.getBoundingClientRect()
  if (!c.width || !c.height) { if (spot.value) spot.value = null; return }
  const cs = getComputedStyle(card)
  const drop = (parseFloat(cs.getPropertyValue('--close-corner-ratio')) || 0) * c.width
  // Number.isFinite, not ||: a card may set --close-inset: 0px on purpose.
  const tuned = parseFloat(cs.getPropertyValue('--close-inset'))
  const inset = props.inset ?? (Number.isFinite(tuned) ? tuned : INSET)
  const el = button.value
  const half = (el?.offsetWidth || 48) / 2
  const radius = (el ? parseFloat(getComputedStyle(el, '::before').width) : 0) / 2 || 20
  const fixed = getComputedStyle(h).position === 'static'
  let x: number
  let y: number
  let from: number
  let width: number
  if (fixed) {
    x = c.right - inset
    y = c.top + inset + drop
    from = 0
    width = innerWidth
  } else {
    const b = h.getBoundingClientRect()
    x = c.right - inset - b.left - h.clientLeft + h.scrollLeft
    y = c.top + inset + drop - b.top - h.clientTop + h.scrollTop
    from = h.scrollLeft
    width = h.clientWidth
  }
  const clamp = (v: number, lo: number, hi: number) => Math.min(Math.max(v, lo), Math.max(lo, hi))
  // The medallion on the corner, whole on screen; the tap box around it, also on screen.
  const mx = Math.round(clamp(x, from + radius + EDGE, from + width - radius - EDGE))
  const my = Math.round(Math.max(y, radius + EDGE))
  const left = Math.round(clamp(mx, from + half, from + width - half))
  const top = Math.round(Math.max(my, half))
  const next = { left, top, ox: mx - left, oy: my - top, fixed }
  const s = spot.value
  if (!s || s.left !== next.left || s.top !== next.top || s.ox !== next.ox || s.oy !== next.oy || s.fixed !== next.fixed) spot.value = next
}

onMounted(() => {
  card = anchor.value?.parentElement ?? null
  host.value = card?.parentElement ?? null
  if (!card || !host.value) return
  nextTick(place)
  observer = new ResizeObserver(() => place())
  observer.observe(card)
  observer.observe(host.value)
  addEventListener('resize', place)
  // Entrance animations (a pop or a slide) move the card without resizing it: follow them closely
  // for a moment, then check a few times a second for anything else that nudges it.
  const start = performance.now()
  const follow = () => { place(); if (performance.now() - start < 900) frame = requestAnimationFrame(follow) }
  frame = requestAnimationFrame(follow)
  timer = setInterval(place, 250)
})
onBeforeUnmount(() => {
  observer?.disconnect()
  removeEventListener('resize', place)
  cancelAnimationFrame(frame)
  clearInterval(timer)
})
</script>

<template>
  <span ref="anchor" class="modal-close-anchor" hidden />
  <Teleport v-if="host" :to="host">
    <button
      ref="button"
      type="button"
      class="modal-close"
      :class="{ 'is-placed': !!spot }"
      :style="spot ? { position: spot.fixed ? 'fixed' : 'absolute', left: `${spot.left}px`, top: `${spot.top}px`, '--mx': `${spot.ox}px`, '--my': `${spot.oy}px` } : undefined"
      :aria-label="label ?? t('common.close')"
      :disabled="disabled"
      :data-testid="testid"
      @click.stop="emit('close')"
    ><span class="modal-close-x" aria-hidden="true" /></button>
  </Teleport>
</template>

<style scoped>
.modal-close-anchor { display: none !important; }
/* A 48 px target (v7.79), out of the backdrop's flow from the first frame (an in-flow button would nudge a
 * centred card), hidden until it knows where the corner is. */
.modal-close {
  position: absolute;
  left: 0;
  top: 0;
  z-index: 6;
  display: grid;
  place-items: center;
  width: 48px;
  height: 48px;
  margin: 0;
  padding: 0;
  border: 0;
  background: none;
  color: #3a2200;
  cursor: pointer;
  visibility: hidden;
  transform: translate(-50%, -50%);
  -webkit-tap-highlight-color: transparent;
}
.modal-close.is-placed { visibility: visible; }
/* The route preview's medallion (draft.vue .preview-chevron), same recipe - v7.79 one size up (40 / 34 px). */
.modal-close::before {
  content: '';
  position: absolute;
  left: 50%;
  top: 50%;
  width: 40px;
  height: 40px;
  box-sizing: border-box;
  transform: translate(calc(-50% + var(--mx, 0px)), calc(-50% + var(--my, 0px)));
  border: 2px solid #3b2a08;
  border-radius: 50%;
  background: radial-gradient(circle at 35% 30%, #fff1b8 0%, #efb93a 55%, #a8730f 100%);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.65), inset 0 -2px 3px rgba(91, 61, 12, 0.45), 0 0 0 2px rgba(239, 185, 58, 0.35), 0 3px 6px rgba(15, 25, 40, 0.5);
  transition: transform 0.15s ease, filter 0.15s ease;
}
/* The ✕ is drawn (a glyph varies by font): two bars, the chevron's stroke and colour. */
.modal-close-x { position: relative; width: 16px; height: 16px; transform: translate(var(--mx, 0px), var(--my, 0px)); }
.modal-close-x::before,
.modal-close-x::after {
  content: '';
  position: absolute;
  left: -1px;
  right: -1px;
  top: 50%;
  height: 3.5px;
  margin-top: -1.75px;
  border-radius: 1px;
  background: currentColor;
  transform: rotate(45deg);
}
.modal-close-x::after { transform: rotate(-45deg); }
.modal-close:hover::before { filter: brightness(1.08); }
.modal-close:active::before { transform: translate(calc(-50% + var(--mx, 0px)), calc(-50% + var(--my, 0px))) scale(0.94); }
.modal-close:focus-visible { outline: none; }
.modal-close:focus-visible::before { outline: 2px solid #326f83; outline-offset: 2px; }
.modal-close:disabled { cursor: default; }
.modal-close:disabled::before { filter: saturate(0.35); opacity: 0.6; }
@media (max-width: 700px) {
  .modal-close::before { width: 34px; height: 34px; }
  .modal-close-x { width: 14px; height: 14px; }
  .modal-close-x::before, .modal-close-x::after { height: 3px; margin-top: -1.5px; }
}
@media (prefers-reduced-motion: reduce) { .modal-close::before { transition: none; } }
</style>
